
import { DebtItem, MonthData, PayoffMethod } from './debtCalculatorTypes';

export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

export const calculatePayoffPlan = (
  debts: DebtItem[],
  monthlyPayment: number,
  payoffMethod: PayoffMethod
): { plan: MonthData[], totalInterest: number, payoffTime: number } => {
  // Validation check
  if (debts.some(debt => debt.balance <= 0 || debt.interestRate < 0 || debt.minPayment < 0)) {
    throw new Error("Invalid debt values");
  }

  const totalMinPayment = debts.reduce((sum, debt) => sum + debt.minPayment, 0);
  
  if (monthlyPayment < totalMinPayment) {
    throw new Error(`Monthly payment must be at least ${formatCurrency(totalMinPayment)}`);
  }

  // Create a deep copy of debts to work with
  let workingDebts = debts.map(debt => ({ ...debt }));
  
  // Sort debts based on strategy
  if (payoffMethod === "avalanche") {
    // Sort by highest interest rate first
    workingDebts.sort((a, b) => b.interestRate - a.interestRate);
  } else if (payoffMethod === "snowball") {
    // Sort by lowest balance first
    workingDebts.sort((a, b) => a.balance - b.balance);
  }
  
  let plan: MonthData[] = [];
  let totalMonths = 0;
  let totalInterestPaid = 0;
  
  // While there are still debts to pay off
  while (workingDebts.length > 0) {
    totalMonths++;
    let remainingPayment = monthlyPayment;
    let monthData: MonthData = { month: totalMonths, debts: [] };
    
    // First, make minimum payments on all debts
    for (let i = 0; i < workingDebts.length; i++) {
      const debt = workingDebts[i];
      const monthlyInterest = (debt.balance * (debt.interestRate / 100)) / 12;
      totalInterestPaid += monthlyInterest;
      
      // Add interest to balance
      debt.balance += monthlyInterest;
      
      // Make minimum payment or pay off debt if balance is less than minimum
      const payment = Math.min(debt.balance, debt.minPayment);
      debt.balance -= payment;
      remainingPayment -= payment;
      
      monthData.debts.push({
        name: debt.name,
        startingBalance: debt.balance + payment,
        payment,
        interest: monthlyInterest,
        remainingBalance: debt.balance
      });
    }
    
    // Apply any remaining payment to the first debt in the list (highest interest or lowest balance)
    if (workingDebts.length > 0 && remainingPayment > 0) {
      const targetDebt = workingDebts[0];
      const additionalPayment = Math.min(targetDebt.balance, remainingPayment);
      
      targetDebt.balance -= additionalPayment;
      monthData.debts[0].payment += additionalPayment;
      monthData.debts[0].remainingBalance = targetDebt.balance;
    }
    
    // Remove any debts that have been paid off
    workingDebts = workingDebts.filter(debt => debt.balance > 0);
    
    plan.push(monthData);
    
    // Avoid infinite loops for edge cases
    if (totalMonths > 600) { // 50 years limit
      break;
    }
  }
  
  return {
    plan,
    totalInterest: totalInterestPaid,
    payoffTime: totalMonths
  };
};
